/**
 * Voters — the single adapter to api.command and api.query.
 *
 * Every voter request in the workspace goes through here, and every response is read through the
 * pure readers below, which accept camelCase and PascalCase and the `{ statusCode, message, data }`
 * envelope or a bare body. Two backends are live at once, so the capability probes decide which
 * calls exist: a 404/405 on `GET Voters/capabilities` means "this build predates the workspace" and
 * yields BASELINE — anything else is rethrown, because a 500 is not proof a feature is absent.
 */

import type { AxiosInstance } from 'axios';

import { httpStatusOf } from '@/app/live-election/engine/capability';
import { commandApi, queryApi } from '@/lib/api-client';

import { type IDuplicateMatch, pick, readDuplicateMatches } from '../engine/voter-errors';
import type { IVoterPayload } from '../engine/voter-rules';

export const VOTERS_BASE = '/api/v1/i-elect/Voters';
export const IMPORTS_BASE = '/api/v1/i-elect/VoterImports';

// ── Envelope ────────────────────────────────────────────────────────────────────────────────

const ENVELOPE_KEYS = new Set(['statuscode', 'message', 'data', 'issuccessful', 'errorcode', 'errors', 'title', 'traceid']);

/**
 * `{ data }` / `{ Data }` → the payload; a bare body passes through. An object is an envelope when it
 * has a data key and nothing BUT envelope keys — so a bare DTO that happens to own a `data` field is
 * never unwrapped by mistake.
 */
export const unwrapEnvelope = <T>(body: unknown): T => {
	if (body && typeof body === 'object' && !Array.isArray(body)) {
		const record = body as Record<string, unknown>;
		const keys = Object.keys(record);
		const hasData = 'data' in record || 'Data' in record;
		if (hasData && keys.every((k) => ENVELOPE_KEYS.has(k.toLowerCase()))) return (record.data ?? record.Data) as T;
	}
	return body as T;
};

const str = (v: unknown): string => (typeof v === 'string' ? v : v === null || v === undefined ? '' : String(v));
const num = (v: unknown, fallback = 0): number => {
	const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
	return Number.isFinite(n) ? n : fallback;
};
const numOrNull = (v: unknown): number | null => {
	const n = num(v, NaN);
	return Number.isFinite(n) ? n : null;
};
const bool = (v: unknown): boolean => v === true || v === 'true';

// ── Capabilities ────────────────────────────────────────────────────────────────────────────

export type CommandFeature = 'create-v2' | 'duplicate-check' | 'import-batches' | 'phone-reveal' | 'edit-delete';
export type QueryFeature = 'register-list' | 'masked-detail';

export interface IVoterCapabilities {
	/** `false` when the command probe 404'd — the legacy backend. */
	commandV2: boolean;
	queryV2: boolean;
	createV2: boolean;
	duplicateCheck: boolean;
	importBatches: boolean;
	phoneReveal: boolean;
	editDelete: boolean;
	registerList: boolean;
	maskedDetail: boolean;
	/** Rows per import chunk the server accepts; the client sends min(this, 2,000). */
	maxChunkRows: number;
}

export const DEFAULT_CHUNK_ROWS = 2000;

export const BASELINE_CAPABILITIES: IVoterCapabilities = {
	commandV2: false,
	queryV2: false,
	createV2: false,
	duplicateCheck: false,
	importBatches: false,
	phoneReveal: false,
	editDelete: false,
	registerList: false,
	maskedDetail: false,
	maxChunkRows: DEFAULT_CHUNK_ROWS,
};

const readFeatures = (body: unknown): Set<string> => {
	const raw = pick(unwrapEnvelope(body), 'features');
	return new Set(Array.isArray(raw) ? raw.map((f) => str(f).toLowerCase()) : []);
};

export const isRouteAbsent = (error: unknown): boolean => {
	const status = httpStatusOf(error);
	return status === 404 || status === 405;
};

/** One probe's body → its half of the capability set. `null` body = the route is absent. */
export const readCommandCapabilities = (body: unknown | null): Partial<IVoterCapabilities> => {
	if (body === null) return {};
	const f = readFeatures(body);
	const maxChunk = num(pick(unwrapEnvelope(body), 'maxChunkRows'), DEFAULT_CHUNK_ROWS);
	return {
		commandV2: true,
		createV2: f.has('create-v2'),
		duplicateCheck: f.has('duplicate-check'),
		importBatches: f.has('import-batches'),
		phoneReveal: f.has('phone-reveal'),
		editDelete: f.has('edit-delete'),
		maxChunkRows: Math.max(1, Math.min(maxChunk, DEFAULT_CHUNK_ROWS)),
	};
};

export const readQueryCapabilities = (body: unknown | null): Partial<IVoterCapabilities> => {
	if (body === null) return {};
	const f = readFeatures(body);
	return { queryV2: true, registerList: f.has('register-list'), maskedDetail: f.has('masked-detail') };
};

const probe = async (client: AxiosInstance): Promise<unknown | null> => {
	try {
		const { data } = await client.get(`${VOTERS_BASE}/capabilities`);
		return data;
	} catch (error) {
		if (isRouteAbsent(error)) return null;
		throw error;
	}
};

/**
 * Both probes, independently: api.command and api.query roll separately, so one may be v2 while the
 * other is still baseline. A failure other than 404/405 on one probe degrades only that half.
 */
export const fetchVoterCapabilities = async (): Promise<IVoterCapabilities> => {
	const [command, query] = await Promise.allSettled([probe(commandApi), probe(queryApi)]);
	return {
		...BASELINE_CAPABILITIES,
		...(command.status === 'fulfilled' ? readCommandCapabilities(command.value) : {}),
		...(query.status === 'fulfilled' ? readQueryCapabilities(query.value) : {}),
	};
};

// ── Create / duplicate check / edit / delete / reveal ───────────────────────────────────────

export interface ICreateVoterRequest extends IVoterPayload {
	allowDuplicate?: boolean;
	duplicateOverrideReason?: string;
}

export const createVoter = async (payload: ICreateVoterRequest): Promise<{ id: string }> => {
	const { data } = await commandApi.post(VOTERS_BASE, payload);
	const body = unwrapEnvelope<unknown>(data);
	return { id: str(pick(body, 'id') ?? pick(body, 'voterId')) };
};

export interface IDuplicateCheckRequest {
	phoneNumber?: string;
	voterRegistrationNumber?: string;
	excludeVoterId?: string;
}

export interface IDuplicateCheckResult {
	total: number;
	matches: IDuplicateMatch[];
}

export const readDuplicateCheck = (body: unknown): IDuplicateCheckResult => {
	const data = unwrapEnvelope<unknown>(body);
	const matches = readDuplicateMatches(pick(data, 'matches'));
	return { total: num(pick(data, 'total'), matches.length), matches };
};

export const checkDuplicates = async (request: IDuplicateCheckRequest, signal?: AbortSignal): Promise<IDuplicateCheckResult> => {
	const { data } = await commandApi.post(`${VOTERS_BASE}/duplicate-check`, request, { signal });
	return readDuplicateCheck(data);
};

/** `expectedUpdatedAt` is required (409 STALE_VOTER on mismatch); a blank `phoneNumber` keeps the stored one. */
export interface IUpdateVoterRequest extends IVoterPayload {
	expectedUpdatedAt: string;
}

/** 200 with message "Changes saved" or "Nothing to change" — the message is returned for the toast. */
export const updateVoter = async (id: string, payload: IUpdateVoterRequest): Promise<{ message: string }> => {
	const { data } = await commandApi.put(`${VOTERS_BASE}/${encodeURIComponent(id)}`, payload);
	return { message: str(pick(data, 'message')) };
};

export const deleteVoter = async (id: string, reason: string): Promise<void> => {
	await commandApi.delete(`${VOTERS_BASE}/${encodeURIComponent(id)}`, { data: { reason } });
};

/** Logged server-side. Phone only — the detail already carries the email. */
export const revealVoterPhone = async (id: string): Promise<{ phoneNumber: string }> => {
	const { data } = await commandApi.post(`${VOTERS_BASE}/${encodeURIComponent(id)}/reveal-phone`, {});
	const body = unwrapEnvelope<unknown>(data);
	return { phoneNumber: str(pick(body, 'phoneNumber')) };
};

// ── Register (api.query) ────────────────────────────────────────────────────────────────────

export type VoterSource = 'Legacy' | 'Manual' | 'Import';

export const readSource = (v: unknown): VoterSource => {
	if (v === 1 || /^manual$/i.test(str(v))) return 'Manual';
	if (v === 2 || /^import$/i.test(str(v))) return 'Import';
	return 'Legacy';
};

export interface IVoterRow {
	id: string;
	fullName: string;
	maskedPhone: string;
	pollingUnitCode: string;
	wardCode: string;
	lgaCode: string;
	stateCode: string;
	source: VoterSource;
	importNumber: number | null;
	importBatchId: string;
	createdByName: string;
	createdAt: string;
}

export const readVoterRow = (raw: unknown): IVoterRow => ({
	id: str(pick(raw, 'id')),
	// The full name where the parts are present; `displayName` ("Adaeze O.") only as a fallback.
	fullName:
		str(pick(raw, 'fullName')).trim() ||
		[pick(raw, 'firstName'), pick(raw, 'middleName'), pick(raw, 'lastName')]
			.map(str)
			.filter((part) => part.trim())
			.join(' ')
			.trim() ||
		str(pick(raw, 'displayName')).trim(),
	maskedPhone: str(pick(raw, 'maskedPhone')),
	pollingUnitCode: str(pick(raw, 'pollingUnitCode')),
	wardCode: str(pick(raw, 'wardCode')),
	lgaCode: str(pick(raw, 'lgaCode')),
	stateCode: str(pick(raw, 'stateCode')),
	source: readSource(pick(raw, 'source')),
	importNumber: numOrNull(pick(raw, 'importNumber')),
	importBatchId: str(pick(raw, 'importBatchId')),
	createdByName: str(pick(raw, 'createdByName')),
	createdAt: str(pick(raw, 'createdAt')),
});

export interface IPage<T> {
	rows: T[];
	total: number;
	pageNumber: number;
	pageSize: number;
}

/** Paged shapes seen across api.query: `{ data: [...], totalCount }`, `{ items, total }`, a bare array. */
export const readPage = <T>(body: unknown, readRow: (raw: unknown) => T, pageNumber: number, pageSize: number): IPage<T> => {
	const outer = (body ?? {}) as Record<string, unknown>;
	const inner = unwrapEnvelope<unknown>(body);
	let rows: unknown[] = [];
	let container: unknown = outer;
	if (Array.isArray(inner)) rows = inner;
	else if (inner && typeof inner === 'object') {
		container = inner;
		const list = pick(inner, 'items') ?? pick(inner, 'data') ?? pick(inner, 'rows');
		if (Array.isArray(list)) rows = list;
	}
	const total = num(
		pick(container, 'totalCount') ?? pick(container, 'total') ?? pick(outer, 'totalCount') ?? pick(outer, 'total'),
		rows.length
	);
	return {
		rows: rows.map(readRow),
		total,
		pageNumber: num(pick(container, 'pageNumber') ?? pick(container, 'currentPage'), pageNumber),
		pageSize: num(pick(container, 'pageSize'), pageSize),
	};
};

export type RegisterSearchField = 'auto' | 'name' | 'phone' | 'vin';

export interface IRegisterParams {
	search: string;
	searchField: RegisterSearchField;
	stateCode: string;
	lgaCode: string;
	wardCode: string;
	pollingUnitCode: string;
	source: '' | VoterSource;
	importBatchId: string;
	pageNumber: number;
	pageSize: number;
}

export const REGISTER_MAX_PAGE_SIZE = 100;

/** Blank filters are omitted rather than sent as '' — an empty string is a filter on some builds. */
export const toRegisterBody = (params: IRegisterParams): Record<string, unknown> => {
	const body: Record<string, unknown> = {
		pageNumber: Math.max(1, params.pageNumber),
		pageSize: Math.max(1, Math.min(REGISTER_MAX_PAGE_SIZE, params.pageSize)),
	};
	const set = (key: string, value: string) => {
		if (value.trim()) body[key] = value.trim();
	};
	// The server auto-detects phone / VIN / name from the text — there is no searchField.
	set('search', params.search);
	set('stateCode', params.stateCode);
	set('lgaCode', params.lgaCode);
	set('wardCode', params.wardCode);
	set('pollingUnitCode', params.pollingUnitCode);
	set('source', params.source);
	set('importBatchId', params.importBatchId);
	return body;
};

export const fetchRegister = async (params: IRegisterParams, signal?: AbortSignal): Promise<IPage<IVoterRow>> => {
	const { data } = await queryApi.post(`${VOTERS_BASE}/register`, toRegisterBody(params), { signal });
	return readPage(data, readVoterRow, params.pageNumber, params.pageSize);
};

export interface IVoterDetail extends IVoterRow {
	firstName: string;
	middleName: string;
	lastName: string;
	maskedEmail: string;
	hasEmail: boolean;
	voterRegistrationNumber: string;
	gender: string;
	dateOfBirth: string;
	updatedAt: string;
	duplicateOverrideReason: string;
	canReveal: boolean;
}

export const readVoterDetail = (body: unknown): IVoterDetail => {
	const raw = unwrapEnvelope<unknown>(body);
	const row = readVoterRow(raw);
	const maskedEmail = str(pick(raw, 'maskedEmail') ?? pick(raw, 'email'));
	return {
		...row,
		firstName: str(pick(raw, 'firstName')),
		middleName: str(pick(raw, 'middleName')),
		lastName: str(pick(raw, 'lastName')),
		maskedEmail,
		hasEmail: pick(raw, 'hasEmail') === undefined ? !!maskedEmail : bool(pick(raw, 'hasEmail')),
		voterRegistrationNumber: str(pick(raw, 'voterRegistrationNumber') ?? pick(raw, 'maskedVin')),
		gender: str(pick(raw, 'gender')),
		dateOfBirth: str(pick(raw, 'dateOfBirth')).slice(0, 10),
		updatedAt: str(pick(raw, 'updatedAt')),
		duplicateOverrideReason: str(pick(raw, 'duplicateOverrideReason')),
		canReveal: bool(pick(raw, 'canReveal')),
	};
};

export const fetchVoterDetail = async (id: string): Promise<IVoterDetail> => {
	const { data } = await queryApi.get(`${VOTERS_BASE}/${encodeURIComponent(id)}`);
	return readVoterDetail(data);
};

// ── Import batches (api.command) ────────────────────────────────────────────────────────────

export type ImportBatchStatus = 'Open' | 'Completed' | 'Abandoned';

export interface IImportBatch {
	id: string;
	number: number;
	fileName: string;
	status: ImportBatchStatus;
	/** The server's own verdict, when it sends one — preferred over the client derivation. */
	serverDisplayStatus: ImportDisplayStatus | null;
	createdByName: string;
	createdAt: string;
	lastActivityAt: string;
	completedAt: string;
	totalRows: number;
	expectedRowCount: number;
	expectedChunkCount: number;
	chunksReceived: number;
	nextChunkIndex: number;
	addedCount: number;
	/** Every row that did not import — register duplicates included. */
	failedCount: number;
}

export const readBatchStatus = (v: unknown): ImportBatchStatus => {
	if (v === 1 || /^completed$/i.test(str(v))) return 'Completed';
	if (v === 2 || /^abandoned$/i.test(str(v))) return 'Abandoned';
	return 'Open';
};

const DISPLAY_STATUS: Record<string, ImportDisplayStatus> = {
	inprogress: 'In progress',
	incomplete: 'Incomplete',
	completed: 'Completed',
	completedwithissues: 'Completed with issues',
};

export const readImportBatch = (body: unknown): IImportBatch => {
	const raw = unwrapEnvelope<unknown>(body);
	const display =
		DISPLAY_STATUS[
			str(pick(raw, 'displayStatus'))
				.replace(/[\s_-]/g, '')
				.toLowerCase()
		] ?? null;
	const chunksReceived = num(pick(raw, 'chunksReceived'));
	return {
		id: str(pick(raw, 'id')),
		number: num(pick(raw, 'number')),
		fileName: str(pick(raw, 'fileName')),
		status: readBatchStatus(pick(raw, 'status')),
		serverDisplayStatus: display,
		createdByName: str(pick(raw, 'createdByName')),
		createdAt: str(pick(raw, 'createdAt')),
		lastActivityAt: str(pick(raw, 'lastActivityAt')),
		completedAt: str(pick(raw, 'completedAt')),
		totalRows: num(pick(raw, 'totalRows')),
		expectedRowCount: num(pick(raw, 'expectedRowCount')),
		expectedChunkCount: num(pick(raw, 'expectedChunkCount')),
		chunksReceived,
		nextChunkIndex: num(pick(raw, 'nextChunkIndex'), chunksReceived),
		addedCount: num(pick(raw, 'addedCount')),
		failedCount: num(pick(raw, 'failedCount')),
	};
};

/** The fields the server maps. Reference-only columns (state name, …) are never sent. */
export const IMPORT_MAPPED_FIELDS = [
	'firstName',
	'middleName',
	'lastName',
	'phoneNumber',
	'email',
	'voterRegistrationNumber',
	'gender',
	'dateOfBirth',
	'pollingUnitCode',
] as const;

export interface ICreateImportRequest {
	fileName: string;
	/** Omitted when SubtleCrypto was unavailable — never sent as ''. */
	fileSha256?: string;
	headers: string[];
	/** field → zero-based column index in `headers`. */
	columnMapping: Record<string, number>;
	expectedRowCount: number;
	expectedChunkCount: number;
}

/** One row on its way up: its sheet row number and its cells, every cell a string. */
export interface IChunkRow {
	rowNumber: number;
	cells: string[];
}

/** What a chunk PUT says. Per-row outcomes are NOT here — they come from `GET …/failures`. */
export interface IChunkResult {
	chunkIndex: number;
	rowCount: number;
	added: number;
	/** Every row that did not import, register duplicates included (added + failed = rowCount). */
	failed: number;
	replayed: boolean;
}

export const readChunkResult = (body: unknown): IChunkResult => {
	const raw = unwrapEnvelope<unknown>(body);
	return {
		chunkIndex: num(pick(raw, 'chunkIndex')),
		rowCount: num(pick(raw, 'rowCount')),
		added: num(pick(raw, 'added')),
		failed: num(pick(raw, 'failed')),
		replayed: bool(pick(raw, 'replayed')),
	};
};

export interface IImportFailure {
	rowNumber: number;
	reasonCode: string;
	reason: string;
	matchedVoterId: string;
	cells: string[];
}

export const readImportFailure = (raw: unknown): IImportFailure => {
	const cells = pick(raw, 'cells');
	return {
		rowNumber: num(pick(raw, 'rowNumber')),
		reasonCode: str(pick(raw, 'reasonCode')) || 'UNKNOWN',
		reason: str(pick(raw, 'reason')),
		matchedVoterId: str(pick(raw, 'matchedVoterId')),
		cells: Array.isArray(cells) ? cells.map(str) : [],
	};
};

export const FAILURES_PAGE_SIZE = 100;

export const fetchImportFailures = async (
	id: string,
	pageNumber: number,
	pageSize = FAILURES_PAGE_SIZE
): Promise<IPage<IImportFailure>> => {
	const { data } = await commandApi.get(
		`${IMPORTS_BASE}/${encodeURIComponent(id)}/failures?pageNumber=${pageNumber}&pageSize=${pageSize}`
	);
	return readPage(data, readImportFailure, pageNumber, pageSize);
};

/** Every failure of a batch, page by page (100 a page). `maxPages` bounds a pathological file. */
export const fetchAllImportFailures = async (id: string, maxPages = 500): Promise<IImportFailure[]> => {
	const all: IImportFailure[] = [];
	for (let page = 1; page <= maxPages; page++) {
		const result = await fetchImportFailures(id, page);
		all.push(...result.rows);
		if (!result.rows.length || all.length >= result.total) break;
	}
	return all;
};

export interface ICreatedBatch {
	id: string;
	number: number;
	/** Earlier batches of the same file (by SHA-256) — non-empty means "imported before". */
	sameFileNumbers: number[];
}

export const createImportBatch = async (request: ICreateImportRequest): Promise<ICreatedBatch> => {
	const { data } = await commandApi.post(IMPORTS_BASE, request);
	const body = unwrapEnvelope<unknown>(data);
	const same = pick(body, 'sameFileBatches');
	return {
		id: str(pick(body, 'id')),
		number: num(pick(body, 'number')),
		sameFileNumbers: Array.isArray(same) ? same.map((b) => num(pick(b, 'number'))).filter((n) => n > 0) : [],
	};
};

export const putImportChunk = async (
	id: string,
	chunkIndex: number,
	body: { firstRowNumber: number; cells: string[][] }
): Promise<IChunkResult> => {
	const { data } = await commandApi.put(`${IMPORTS_BASE}/${encodeURIComponent(id)}/chunks/${chunkIndex}`, body);
	return readChunkResult(data);
};

export const fetchImportBatch = async (id: string): Promise<IImportBatch> => {
	const { data } = await commandApi.get(`${IMPORTS_BASE}/${encodeURIComponent(id)}`);
	return readImportBatch(data);
};

export const completeImportBatch = async (id: string): Promise<void> => {
	await commandApi.post(`${IMPORTS_BASE}/${encodeURIComponent(id)}/complete`, {});
};

export const abandonImportBatch = async (id: string): Promise<void> => {
	await commandApi.post(`${IMPORTS_BASE}/${encodeURIComponent(id)}/abandon`, {});
};

export const fetchImportBatches = async (pageNumber: number, pageSize: number): Promise<IPage<IImportBatch>> => {
	const { data } = await commandApi.get(`${IMPORTS_BASE}?pageNumber=${pageNumber}&pageSize=${pageSize}`);
	return readPage(data, readImportBatch, pageNumber, pageSize);
};

export const fetchImportFailuresCsv = async (id: string): Promise<Blob> => {
	const { data } = await commandApi.get(`${IMPORTS_BASE}/${encodeURIComponent(id)}/failures.csv`, { responseType: 'blob' });
	return data as Blob;
};

// ── History status ──────────────────────────────────────────────────────────────────────────

export type ImportDisplayStatus = 'Completed' | 'Completed with issues' | 'In progress' | 'Incomplete';

/** No chunk for this long and still Open → nobody is sending it any more. */
export const STALE_IMPORT_MS = 15 * 60 * 1000;

export const importDisplayStatus = (batch: IImportBatch, now: number = Date.now()): ImportDisplayStatus => {
	if (batch.serverDisplayStatus) return batch.serverDisplayStatus;
	if (batch.status === 'Completed') return batch.failedCount > 0 ? 'Completed with issues' : 'Completed';
	if (batch.status === 'Abandoned') return 'Incomplete';
	const last = Date.parse(batch.lastActivityAt || batch.createdAt);
	if (Number.isFinite(last) && now - last > STALE_IMPORT_MS) return 'Incomplete';
	return 'In progress';
};
